import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { Download, Copy, FileText, ShieldCheck, AlertCircle, RefreshCw, Sparkles, Save, Terminal, CheckCircle2, Lightbulb, Rocket, Heart, Palette, AlertTriangle, FileType, FileSpreadsheet, X, ChevronDown } from 'lucide-react';
import { exportComparisonToExcel } from '../../../utils/exportExcel';
import { exportTxt } from '../../../utils/fileExporter';
import { apiExportDocx, apiExportPptx, apiExportDocxStateless, apiExportPptxStateless } from '../../../services/api/docxApi';
import type { ParagraphDisplay, ParseStructure } from '../../../services/api/docxApi';
import { extractTextXmls } from '../../../utils/clientDocxUnzip';
import { replaceXmlsAndRezip, downloadBuffer, DOCX_MIME, PPTX_MIME } from '../../../utils/clientDocxRezip';
import { useDevMode } from '../../../hooks/useDevMode';
import { AuditDetails } from '../../../types';
import { parseMatchedRulesString, highlightTermsInText } from '../../../utils/highlightTerms';
import { useAuth } from '../../../contexts/AuthContext';
import LanguagePicker from '../../../components/commercial/OnboardingTour/LanguagePicker';
import InlineCostBadge from '../../../components/commercial/InlineCostBadge';
import { usePreflightCost } from '../../../hooks/usePreflightCost';

// ============================================
// FORMATTING HELPERS (ported from index.html)
// ============================================

function isLightColor(hex: string): boolean {
    if (hex.length !== 6) return false;
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    if (isNaN(r) || isNaN(g) || isNaN(b)) return false;
    // Relative luminance — 0.7 threshold catches white/very pale.
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return lum > 0.7;
}

function sigToStyle(sig: string): string {
    if (sig === 'default') return '';
    const styles: string[] = [];
    let textColor: string | null = null;
    for (const p of sig.split('+')) {
        if (p === 'B') styles.push('font-weight:bold');
        else if (p === 'I') styles.push('font-style:italic');
        else if (p.startsWith('C:')) {
            textColor = p.substring(2);
            styles.push('color:#' + textColor);
        }
        else if (p.startsWith('S:')) {
            // DOCX uses half-points (w:sz val="24" = 12pt, typically 16-120 range)
            // PPTX uses hundredths of point (a:rPr sz="2400" = 24pt, typically 1000-7200 range)
            const raw = parseInt(p.substring(2));
            const pt = raw >= 200 ? raw / 100 : raw / 2;
            if (pt > 14) styles.push('font-size:' + pt + 'pt');
        }
        else if (p === 'U') styles.push('text-decoration:underline');
        else if (p === 'STR') styles.push('text-decoration:line-through');
        // HL: (background highlight from DOCX) intentionally dropped — readability over fidelity.
        // Replace với black text-stroke khi text màu sáng (xem dưới).
        else if (p === 'CAP') styles.push('text-transform:uppercase');
    }
    // Text outline for light-colored text (reading view): drop bg, add black stroke.
    if (textColor && isLightColor(textColor)) {
        styles.push('-webkit-text-stroke: 1.2px #000');
        styles.push('text-shadow: -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 0 2px #000');
        styles.push('paint-order: stroke fill');
    }
    return styles.join(';');
}

function escapeHtml(t: string): string {
    return t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// ============================================
// PROPS
// ============================================

interface TranslationExportV2Props {
    sourceSegments: string[];       // backendSegments (flat AI segments)
    aiOutputSegments: string[];     // translated segments (same length)
    fileName: string;
    backendParagraphs: ParagraphDisplay[];
    // Export support — either legacy (fileId) or stateless (originalFile + metadata)
    backendFileId?: string;                      // Legacy flow: KV-based export
    originalFile?: ArrayBuffer;                  // Stateless flow: client-held file
    originalFormat?: 'docx' | 'pptx';            // Stateless flow: format
    backendChartLabels?: string[];               // Stateless flow: chart metadata
    backendChartAiStart?: number;
    backendSmartLabels?: string[];
    backendSmartAiStart?: number;
    backendParseStructure?: ParseStructure;      // Cached parse → server skips re-parse
    backendSegments?: string[];                   // Source segments (needed for export when parseStructure present)
    // Quality check
    qualityResult?: AuditDetails;
    isQualityChecking?: boolean;
    onRunQualityCheck?: () => void;
    qcOutputLang?: string;
    onQcOutputLangChange?: (lang: string) => void;
    projectSourceLang?: string;
    // Legacy quality props
    segmentEvaluations?: Array<{
        sourceIndex?: number;
        score?: number;
        rules?: string;
        errors?: string;
        reviews?: string;
        notes?: string;
    }>;
    overallScore?: number;
    accuracyAnalysis?: string;
    terminologyAnalysis?: string;
    toneAnalysis?: string;
    componentScores?: {
        accuracy?: number;
        terminology?: number;
        tone?: number;
    };
    suggestedPromptUpdate?: string;
    isTruncated?: boolean;
    onBackToSource: () => void;
    onNewSession: () => void;
    onShowDebug?: () => void;
    onRetranslate?: () => void;
    isRetranslating?: boolean;
    autoQualityCheck?: boolean;
    onAutoQualityCheckChange?: (value: boolean) => void;
    matchedRules?: string;
    onUpdateAiOutput?: (newAiOutput: string[]) => void;
    onUpdateQualityResult?: (newResult: AuditDetails) => void;
    batchInfo?: import('../../../types').BatchInfo[];
    onRetryBatch?: (batchIndex: number) => void;
    onRetryAllFailed?: () => void;
}

// ============================================
// MAIN COMPONENT
// ============================================

const TranslationExportV2: React.FC<TranslationExportV2Props> = ({
    sourceSegments,
    aiOutputSegments,
    fileName,
    backendFileId,
    backendParagraphs,
    originalFile,
    originalFormat,
    backendChartLabels,
    backendChartAiStart,
    backendSmartLabels,
    backendSmartAiStart,
    backendParseStructure,
    backendSegments: sourceSegmentsForExport,
    qualityResult,
    isQualityChecking = false,
    onRunQualityCheck,
    qcOutputLang,
    onQcOutputLangChange,
    projectSourceLang = 'vi',
    segmentEvaluations = [],
    overallScore: legacyOverallScore,
    accuracyAnalysis,
    toneAnalysis,
    componentScores: legacyComponentScores,
    suggestedPromptUpdate: legacySuggestedPromptUpdate,
    isTruncated,
    onBackToSource,
    onNewSession,
    onShowDebug,
    onRetranslate,
    isRetranslating = false,
    matchedRules: matchedRulesText,
    onUpdateAiOutput,
    onUpdateQualityResult,
    batchInfo,
    onRetryBatch,
    onRetryAllFailed,
}) => {
    const [pendingAiSegments, setPendingAiSegments] = useState<string[] | null>(null);
    const [pendingQualityResult, setPendingQualityResult] = useState<AuditDetails | null>(null);
    const [isExporting, setIsExporting] = useState(false);
    const [showDebugIO, setShowDebugIO] = useState(false);
    const [showTermHighlight, setShowTermHighlight] = useState(false);
    const [isQCDrawerOpen, setIsQCDrawerOpen] = useState(false);
    const [focusedAiIdx, setFocusedAiIdx] = useState<number | null>(null);
    const { user } = useAuth();
    const tableRef = useRef<HTMLDivElement>(null);
    const loadMoreRef = useRef<HTMLDivElement>(null);

    // Parse terminology rules for source/target highlighting
    const { sourceTerms, targetTerms } = useMemo(() => {
        const rules = parseMatchedRulesString(matchedRulesText || '');
        return {
            sourceTerms: rules.map(r => r.source),
            targetTerms: rules.map(r => r.target),
        };
    }, [matchedRulesText]);

    // Highlight matched terms inside an HTML string (target column uses innerHTML).
    // Must not inject into existing tags — regex only matches outside angle brackets.
    const highlightTermsInHtml = (html: string, terms: string[], cls: string): string => {
        if (!terms.length || !html) return html;
        const sortedTerms = [...terms].sort((a, b) => b.length - a.length);
        for (const term of sortedTerms) {
            if (!term.trim()) continue;
            const esc = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            // Only replace when NOT inside a tag: use negative lookahead for '>'
            const re = new RegExp(`(${esc})(?=[^>]*(<|$))`, 'gi');
            html = html.replace(re, `<span class="${cls}">$1</span>`);
        }
        return html;
    };

    // Progressive loading: show 30 rows initially, load 30 more on scroll
    const BATCH_SIZE = 30;
    const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);

    // Reset visible count when file changes
    useEffect(() => {
        setVisibleCount(BATCH_SIZE);
    }, [fileName]);

    // IntersectionObserver: load more when sentinel is visible
    useEffect(() => {
        const el = loadMoreRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setVisibleCount(prev => prev + BATCH_SIZE);
                }
            },
            { threshold: 0.1 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [backendParagraphs.length]);

    const effectiveBatchInfo = useMemo(() => {
        if (batchInfo && batchInfo.length > 0) return batchInfo;
        if (sourceSegments.length === 0) return undefined;
        const missing = sourceSegments.length - aiOutputSegments.filter(Boolean).length;
        return [{
            index: 0,
            startSegment: 0,
            endSegment: sourceSegments.length,
            segmentCount: sourceSegments.length,
            status: (missing > 0 ? 'error' : 'done') as 'done' | 'error',
            missingCount: missing > 0 ? missing : undefined,
            error: missing > 0 ? `${missing} đoạn bị thiếu` : undefined,
        }];
    }, [batchInfo, sourceSegments, aiOutputSegments]);

    const hasPendingChanges = pendingAiSegments !== null || pendingQualityResult !== null;
    const currentAiSegments = pendingAiSegments || aiOutputSegments;
    const currentQualityResult = pendingQualityResult || qualityResult;

    // Pre-compute inline cost estimates via shared usePreflightCost hook.
    // Each result has {credits, isComputing, error} — error visible in <InlineCostBadge>.
    const qcPreflight = usePreflightCost({
        type: 'qc',
        sourceSegments,
        aiSegments: currentAiSegments,
        enabled: !isQualityChecking,
    });
    const retranslatePreflight = usePreflightCost({
        type: 'retranslate',
        sourceSegments,
        enabled: !isRetranslating,
    });

    // Auto-open drawer khi QC result vừa về
    useEffect(() => {
        if (currentQualityResult?.overallScore !== undefined) {
            setIsQCDrawerOpen(true);
        }
    }, [currentQualityResult?.overallScore]);

    // QC severity types + map aiIdx → issue
    type IssueSeverity = 'critical' | 'major' | 'minor';
    const issueByAiIdx = useMemo(() => {
        const map = new Map<number, {
            severity: IssueSeverity;
            score: number;
            errors?: string;
            issues?: string;
            suggestions?: string;
            reviews?: string;
        }>();
        if (!currentQualityResult?.segmentMappings) return map;
        for (const sm of currentQualityResult.segmentMappings) {
            const hasText = !!(sm.errors?.trim() || sm.issues?.trim() || sm.reviews?.trim());
            if (sm.score >= 8 && !hasText) continue;
            const severity: IssueSeverity = sm.score < 5 ? 'critical' : sm.score < 8 ? 'major' : 'minor';
            // QC agent return sourceIndex 1-based (qualityCheckAgent.ts line 33); aiIdx là 0-based → trừ 1
            const aiIdx0 = sm.sourceIndex - 1;
            map.set(aiIdx0, {
                severity,
                score: sm.score,
                errors: sm.errors,
                issues: sm.issues,
                suggestions: sm.suggestions,
                reviews: sm.reviews,
            });
        }
        return map;
    }, [currentQualityResult]);

    // Parse issue text "[Severity] Category: Description" → {category, description}
    // QC agent output format (qualityCheckAgent.ts line 101): `[${severity}] ${category}: ${description}`
    function parseIssueText(raw: string | undefined): { category?: string; description: string } {
        if (!raw) return { description: '' };
        const match = raw.match(/^\[(\w+)\]\s+([^:]+):\s*(.+)$/s);
        if (match) return { category: match[2].trim(), description: match[3].trim() };
        return { description: raw };
    }

    const sortedIssues = useMemo(() => {
        const sevOrder: Record<IssueSeverity, number> = { critical: 0, major: 1, minor: 2 };
        const arr = Array.from(issueByAiIdx.entries()).map(([aiIdx, info]) => {
            const parsed = parseIssueText(info.errors || info.issues);
            const sm = currentQualityResult?.segmentMappings?.find(m => m.sourceIndex - 1 === aiIdx);
            // Original text = bản dịch AI hiện tại (English). KHÔNG tin sm.notes vì QC agent có thể
            // trả về source Vietnamese thay vì translation English ở cột "original".
            const currentTranslation = currentAiSegments[aiIdx] || '';
            const sourceText = sourceSegments[aiIdx] || '';
            return {
                aiIdx,
                ...info,
                category: parsed.category,
                description: parsed.description,
                sourceText,
                currentTranslation,
                suggestionText: sm?.suggestions || info.suggestions || '',
            };
        });
        arr.sort((a, b) => sevOrder[a.severity] - sevOrder[b.severity] || a.score - b.score);
        return arr;
    }, [issueByAiIdx, currentQualityResult, currentAiSegments, sourceSegments]);

    // Category → màu badge
    const categoryColors: Record<string, string> = {
        Accuracy: 'bg-blue-50 text-blue-700 border-blue-200',
        Terminology: 'bg-purple-50 text-purple-700 border-purple-200',
        Style: 'bg-amber-50 text-amber-700 border-amber-200',
        Fluency: 'bg-violet-50 text-violet-700 border-violet-200',
        Completeness: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        Grammar: 'bg-rose-50 text-rose-700 border-rose-200',
    };
    const getCategoryColor = (cat?: string) =>
        (cat && categoryColors[cat]) || 'bg-slate-50 text-slate-700 border-slate-200';

    const sevColors: Record<IssueSeverity, { text: string; bg: string; border: string; dot: string; label: string }> = {
        critical: { text: 'text-red-700', bg: 'bg-red-50', border: 'border-red-300', dot: 'bg-red-500', label: 'Critical' },
        major: { text: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-300', dot: 'bg-amber-500', label: 'Major' },
        minor: { text: 'text-sky-700', bg: 'bg-sky-50', border: 'border-sky-300', dot: 'bg-sky-500', label: 'Minor' },
    };

    // Build translated text per paragraph for display
    const paragraphData = useMemo(() => {
        return backendParagraphs.map((p, pi) => {
            const sourceGroups = p.groups.map(g => {
                const text = g.wsText !== undefined ? g.wsText : (sourceSegments[g.aiIdx!] || '');
                return { sig: g.sig, text, aiIdx: g.aiIdx };
            });
            const translatedGroups = p.groups.map(g => {
                if (g.wsText !== undefined) return { sig: g.sig, text: g.wsText, aiIdx: g.aiIdx };
                const origText = sourceSegments[g.aiIdx!] || '';
                const transText = currentAiSegments[g.aiIdx!] || '';
                // Preserve whitespace boundaries — AI may strip leading/trailing spaces
                let text = transText;
                if (transText) {
                    const lead = origText.match(/^\s+/)?.[0] || '';
                    const trail = origText.match(/\s+$/)?.[0] || '';
                    if (lead && !/^\s/.test(text)) text = lead + text;
                    if (trail && !/\s$/.test(text)) text = text + trail;
                }
                return { sig: g.sig, text, aiIdx: g.aiIdx };
            });
            return { type: p.type, sourceGroups, translatedGroups, groups: p.groups };
        });
    }, [backendParagraphs, sourceSegments, currentAiSegments]);

    // Map aiIdx → paragraph display index (1-based) để show trong drawer match với row number UI
    const aiIdxToParaDisplay = useMemo(() => {
        const map = new Map<number, number>();
        backendParagraphs.forEach((p, pi) => {
            for (const g of p.groups) {
                if (g.aiIdx !== undefined) map.set(g.aiIdx, pi + 1);
            }
        });
        return map;
    }, [backendParagraphs]);

    // Per-paragraph worst severity (paragraphData-dependent)
    const paraIssueSev = useMemo(() => {
        return paragraphData.map((para) => {
            const sevs = new Set<IssueSeverity>();
            const aiIdxs: number[] = [];
            for (const g of para.groups) {
                if (g.aiIdx !== undefined) {
                    const issue = issueByAiIdx.get(g.aiIdx);
                    if (issue) {
                        sevs.add(issue.severity);
                        aiIdxs.push(g.aiIdx);
                    }
                }
            }
            if (sevs.size === 0) return null;
            const worst: IssueSeverity = sevs.has('critical') ? 'critical' : sevs.has('major') ? 'major' : 'minor';
            return { severity: worst, aiIdxs };
        });
    }, [paragraphData, issueByAiIdx]);

    // Scroll table → row for a given aiIdx, flash highlight
    const scrollToAiIdx = useCallback((aiIdx: number) => {
        setFocusedAiIdx(aiIdx);
        const paraIdx = paragraphData.findIndex(p => p.groups.some(g => g.aiIdx === aiIdx));
        if (paraIdx < 0) return;
        if (paraIdx >= visibleCount) setVisibleCount(paraIdx + 10);
        setTimeout(() => {
            const row = tableRef.current?.querySelector(`tr[data-para-idx="${paraIdx}"]`) as HTMLElement | null;
            row?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 50);
        setTimeout(() => setFocusedAiIdx(null), 2500);
    }, [paragraphData, visibleCount]);

    // ---- Handlers ----

    const handleEditFromContentEditable = useCallback((paraIdx: number) => {
        if (!tableRef.current) return;
        const editableDiv = tableRef.current.querySelector(`[data-para-idx="${paraIdx}"]`) as HTMLElement;
        if (!editableDiv) return;

        const newSegments = [...currentAiSegments];
        const para = backendParagraphs[paraIdx];

        // Read text from spans
        const spans = editableDiv.querySelectorAll('span[data-ai-idx]');
        spans.forEach(span => {
            const aiIdx = parseInt((span as HTMLElement).dataset.aiIdx || '-1');
            if (aiIdx >= 0 && aiIdx < newSegments.length) {
                newSegments[aiIdx] = span.textContent || '';
            }
        });

        // If no spans found (user cleared formatting), read plaintext and put in first non-ws group
        if (spans.length === 0 && para) {
            const plainText = editableDiv.textContent || '';
            const firstAiGroup = para.groups.find(g => g.aiIdx !== undefined);
            if (firstAiGroup) {
                newSegments[firstAiGroup.aiIdx!] = plainText;
            }
        }

        setPendingAiSegments(newSegments);
    }, [currentAiSegments, backendParagraphs]);

    const handleSavePendingChanges = () => {
        if (pendingAiSegments && onUpdateAiOutput) {
            onUpdateAiOutput(pendingAiSegments);
        }
        if (pendingQualityResult && onUpdateQualityResult) {
            onUpdateQualityResult(pendingQualityResult);
        }
        setPendingAiSegments(null);
        setPendingQualityResult(null);
    };

    const confirmBeforeExport = (exportFn: () => void) => {
        if (hasPendingChanges) {
            if (confirm('Bạn có thay đổi chưa lưu. Lưu trước khi export?')) {
                handleSavePendingChanges();
            }
        }
        exportFn();
    };

    const handleExportFile = async () => {
        setIsExporting(true);
        try {
            // STATELESS flow: client has original file + metadata
            if (originalFile && originalFormat && backendChartLabels !== undefined) {
                const isPptx = originalFormat === 'pptx';
                const xmls = await extractTextXmls(originalFile, originalFormat);

                const exportBody = {
                    xmls,
                    translated: currentAiSegments,
                    chartLabels: backendChartLabels,
                    chartAiStart: backendChartAiStart ?? 0,
                    smartLabels: backendSmartLabels ?? [],
                    smartAiStart: backendSmartAiStart ?? 0,
                    // Cached parse → server skips re-parse (fixes DOCX 503)
                    parseStructure: backendParseStructure,
                    segments: sourceSegmentsForExport,
                };
                const modifiedXmls = isPptx
                    ? await apiExportPptxStateless(exportBody)
                    : await apiExportDocxStateless(exportBody);

                const mimeType = isPptx ? PPTX_MIME : DOCX_MIME;
                const outputBuffer = await replaceXmlsAndRezip(originalFile, modifiedXmls, mimeType);
                const outputName = fileName.replace(/\.(docx|pptx)$/i, '_translated.$1');
                downloadBuffer(outputBuffer, outputName, mimeType);
                return;
            }

            // LEGACY flow fallback (KV-based)
            if (!backendFileId) throw new Error('Missing file data. Please re-upload.');
            const isPptx = backendFileId.startsWith('pptx_');
            const blob = isPptx
                ? await apiExportPptx(backendFileId, currentAiSegments)
                : await apiExportDocx(backendFileId, currentAiSegments);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName.replace(/\.(docx|pptx)$/i, '_translated.$1');
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err: any) {
            console.error('[Export] Backend export failed:', err);
            alert('Export thất bại: ' + err.message);
        } finally {
            setIsExporting(false);
        }
    };

    const handleExportExcel = () => {
        exportComparisonToExcel({
            sourceSegments,
            aiSegments: currentAiSegments,
            benchmarkSegments: [],
            fileName,
        });
    };

    const handleCopyAll = () => {
        navigator.clipboard.writeText(currentAiSegments.join('\n'));
    };

    const { isDevMode, handleSecretClick } = useDevMode();

// Quality score helpers
    const overallScore = currentQualityResult?.overallScore ?? legacyOverallScore;
    const componentScores = currentQualityResult ? {
        accuracy: currentQualityResult.componentScores.accuracy,
        fluency: currentQualityResult.componentScores.fluency || 0,
        completeness: currentQualityResult.componentScores.completeness || 0,
        style: currentQualityResult.componentScores.style || currentQualityResult.componentScores.tone || 0
    } : (legacyComponentScores ? {
        accuracy: legacyComponentScores.accuracy,
        fluency: 0,
        completeness: 0,
        style: legacyComponentScores.tone || 0
    } : undefined);

    const suggestedPromptUpdate = currentQualityResult?.suggestedPromptUpdate ?? legacySuggestedPromptUpdate;

    const getScoreTextColor = (score: number) => {
        if (score >= 8) return 'text-emerald-500';
        if (score >= 6) return 'text-amber-500';
        return 'text-red-500';
    };

    // ============================================
    // RENDER
    // ============================================

    return (
        <div className="translate-output flex flex-col h-full animate-in zoom-in-95 duration-500 bg-slate-100 px-4 pt-2" data-mask="true">
            <div className="flex flex-1 min-h-0 gap-5 pb-4">
                {/* Left Column: Rich Text Table — elevated with stronger shadow */}
                <div className="flex-1 flex flex-col min-w-0 bg-white rounded-xl shadow-[0_4px_16px_rgba(15,23,42,0.08)] border border-slate-200/60 overflow-hidden relative z-0">
                    {/* Content Header */}
                    <div className="p-4 border-b border-slate-200 shrink-0 flex items-center gap-4 bg-white relative z-20">
                        <div className="flex-1 min-w-0">
                            <h2 className="text-lg md:text-xl font-black text-slate-800 flex items-center gap-3">
                                <span className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-transparent bg-clip-text truncate block">
                                    {fileName}
                                </span>
                            </h2>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-400">
                            <span>{backendParagraphs.length} paragraphs</span>
                            <span className="w-1 h-1 rounded-full bg-slate-300" />
                            <span>{sourceSegments.length} segments</span>
                        </div>
                    </div>

                    {/* Scrollable Content */}
                    <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-4 relative z-10">
                        {/* AI disclaimer */}
                        <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                            <span>AI có thể sai sót — luôn rà soát bản dịch trước khi sử dụng.</span>
                        </div>
                        {/* Truncation Warning */}
                        {isTruncated && (
                            <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                                <div>
                                    <h4 className="text-sm font-bold text-amber-800 uppercase tracking-wide">Kết quả bị cắt bớt</h4>
                                    <p className="text-sm text-amber-700 mt-1">
                                        Kết quả dịch này chưa hoàn tất do vượt quá giới hạn độ dài.
                                        Vui lòng tăng <strong>Max Output Tokens</strong> trong cài đặt và chạy lại.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Quality Check Summary - 4 Cards */}
                        {overallScore !== undefined && (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <ShieldCheck className="w-5 h-5 text-emerald-600" />
                                        <h3 className="font-bold text-slate-700">Quality Report</h3>
                                        <div className={`px-2.5 py-0.5 rounded-md text-xs font-black ${getScoreTextColor(overallScore)} bg-slate-100 border border-slate-200`}>
                                            OVERALL: {overallScore}/10
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                                    {[
                                        { label: 'Accuracy', score: componentScores?.accuracy || 0, text: currentQualityResult?.accuracyAnalysis || accuracyAnalysis, color: 'blue' },
                                        { label: 'Fluency', score: (componentScores as any)?.fluency || 0, text: currentQualityResult?.fluencyAnalysis || '', color: 'violet' },
                                        { label: 'Completeness', score: (componentScores as any)?.completeness || 0, text: currentQualityResult?.completenessAnalysis || '', color: 'emerald' },
                                        { label: 'Style', score: (componentScores as any)?.style || 0, text: currentQualityResult?.toneAnalysis || toneAnalysis, color: 'amber' },
                                    ].map((item) => (
                                        <div key={item.label} className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col gap-3 shadow-sm hover:shadow-md transition-all">
                                            <div className="flex items-center justify-between">
                                                <span className={`text-[10px] font-black uppercase tracking-wider text-${item.color}-600 bg-${item.color}-50 px-2 py-0.5 rounded border border-${item.color}-100`}>
                                                    {item.label}
                                                </span>
                                                <span className={`text-xl font-black text-${item.color}-600`}>
                                                    {item.score}
                                                </span>
                                            </div>
                                            <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                                                <div
                                                    className={`h-full rounded-full bg-${item.color}-500 transition-all duration-500`}
                                                    style={{ width: `${(item.score || 0) * 10}%` }}
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-xs text-slate-600 leading-relaxed line-clamp-4 min-h-[4.5em]" title={item.text || 'No comment'}>
                                                    {item.text || <span className="text-slate-300 italic">No analysis available</span>}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Suggestion */}
                        {suggestedPromptUpdate && (
                            <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-4 border border-indigo-100">
                                <div className="flex items-start gap-3">
                                    <div className="p-2 bg-indigo-100 rounded-lg shrink-0">
                                        <Sparkles className="w-4 h-4 text-indigo-600" />
                                    </div>
                                    <div>
                                        <h4 className="text-xs font-bold text-indigo-900 uppercase tracking-wider mb-1">IMPROVEMENT SUGGESTION</h4>
                                        <p className="text-sm text-indigo-800 leading-relaxed">
                                            {suggestedPromptUpdate}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Rich Text Comparison Table */}
                        <div ref={tableRef} className="border border-slate-200 rounded-xl overflow-hidden">
                            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider px-4 py-2 bg-slate-50 border-b border-slate-200">
                                Chi tiết bản dịch (Rich Text)
                            </div>
                            <table className="w-full border-collapse">
                                <thead>
                                    <tr className="bg-slate-50 border-b border-slate-200">
                                        <th className="w-8 px-1 py-2 text-[10px] font-black uppercase tracking-wider text-slate-400 text-center border-r border-slate-200" title="Quality issue">QC</th>
                                        <th className="w-10 px-2 py-2 text-[10px] font-black uppercase tracking-wider text-slate-400 text-center border-r border-slate-200">#</th>
                                        <th className="w-1/2 px-3 py-2 text-[10px] font-black uppercase tracking-wider text-slate-400 text-left border-r border-slate-200">Source</th>
                                        <th className="w-1/2 px-3 py-2 text-[10px] font-black uppercase tracking-wider text-slate-400 text-left">Translated</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paragraphData.slice(0, visibleCount).map((para, i) => {
                                        const isMixed = para.sourceGroups.length > 1 &&
                                            new Set(para.sourceGroups.map(g => g.sig)).size > 1;
                                        const isTextbox = para.type === 'textbox';
                                        // hasWhiteText logic dropped 2026-04-29 — replaced by black text-stroke in sigToStyle.

                                        const paraSev = paraIssueSev[i];
                                        const isFocused = focusedAiIdx !== null && para.groups.some(g => g.aiIdx === focusedAiIdx);
                                        const firstIssue = paraSev ? issueByAiIdx.get(paraSev.aiIdxs[0]) : null;
                                        const sevStyle = paraSev ? sevColors[paraSev.severity] : null;

                                        return (
                                            <tr
                                                key={i}
                                                data-para-idx={i}
                                                className={`border-b hover:opacity-90 transition-colors ${
                                                    isFocused ? 'bg-yellow-100 ring-2 ring-yellow-400' :
                                                    isTextbox ? 'bg-amber-50/30 border-b border-slate-200' :
                                                    isMixed ? 'bg-indigo-50/20 border-b border-slate-200' : 'border-b border-slate-200'
                                                }`}
                                            >
                                                <td className="px-1 py-1.5 text-center align-top border-r border-slate-200">
                                                    {paraSev && sevStyle && firstIssue && (
                                                        <button
                                                            onClick={() => {
                                                                setIsQCDrawerOpen(true);
                                                                setFocusedAiIdx(paraSev.aiIdxs[0]);
                                                                setTimeout(() => setFocusedAiIdx(null), 2500);
                                                            }}
                                                            className={`group/qcicon relative inline-flex items-center justify-center w-5 h-5 rounded-full ${sevStyle.dot} text-white text-[10px] font-bold hover:scale-125 transition-transform`}
                                                            title="Click để xem chi tiết"
                                                            aria-label={`${sevStyle.label} issue`}
                                                        >
                                                            !
                                                            {/* Tooltip */}
                                                            <div className="pointer-events-none absolute left-6 top-0 z-50 w-72 opacity-0 group-hover/qcicon:opacity-100 group-hover/qcicon:pointer-events-auto transition-opacity">
                                                                <div className={`${sevStyle.bg} border ${sevStyle.border} rounded-lg shadow-xl p-3 text-left`}>
                                                                    <div className="flex items-center gap-2 mb-1.5">
                                                                        <span className={`w-2 h-2 rounded-full ${sevStyle.dot}`} />
                                                                        <span className={`text-[10px] font-black uppercase tracking-wider ${sevStyle.text}`}>{sevStyle.label}</span>
                                                                        <span className="text-[10px] text-slate-500 ml-auto tabular-nums">{firstIssue.score}/10</span>
                                                                    </div>
                                                                    {firstIssue.errors && (
                                                                        <div className="text-xs text-slate-700 mb-1"><span className="font-semibold">Lỗi: </span>{firstIssue.errors}</div>
                                                                    )}
                                                                    {firstIssue.issues && (
                                                                        <div className="text-xs text-slate-700 mb-1"><span className="font-semibold">Issue: </span>{firstIssue.issues}</div>
                                                                    )}
                                                                    {firstIssue.reviews && (
                                                                        <div className="text-xs text-slate-600 mb-1 italic">{firstIssue.reviews}</div>
                                                                    )}
                                                                    {firstIssue.suggestions && (
                                                                        <div className="text-xs text-emerald-700 mt-1.5 pt-1.5 border-t border-slate-200"><span className="font-semibold">Gợi ý: </span>{firstIssue.suggestions}</div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </button>
                                                    )}
                                                </td>
                                                <td className="px-2 py-1.5 text-center text-[11px] font-mono align-top text-slate-400 border-r border-slate-200">
                                                    {i + 1}
                                                </td>
                                                <td className="px-3 py-1.5 text-sm align-top leading-relaxed text-slate-700 border-r border-slate-200">
                                                    {isTextbox && (
                                                        <span className="inline-block text-[9px] bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded mr-1 font-bold">
                                                            TXT
                                                        </span>
                                                    )}
                                                    {para.sourceGroups.map((g, gi) => {
                                                        const style = sigToStyle(g.sig);
                                                        return (
                                                            <span
                                                                key={gi}
                                                                style={style ? cssStringToObj(style) : undefined}
                                                            >
                                                                {highlightTermsInText(g.text, showTermHighlight ? sourceTerms : [], 'border border-amber-400 rounded px-0.5 animate-pulse')}
                                                            </span>
                                                        );
                                                    })}
                                                </td>
                                                <td className="px-3 py-1.5 text-sm align-top">
                                                    <div
                                                        className="outline-none border border-transparent rounded px-1 py-0.5 min-h-[20px] hover:border-slate-200 focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-100 transition-all leading-relaxed"
                                                        contentEditable
                                                        suppressContentEditableWarning
                                                        data-para-idx={i}
                                                        onBlur={() => handleEditFromContentEditable(i)}
                                                        dangerouslySetInnerHTML={{
                                                            __html: para.translatedGroups.map((g, gi) => {
                                                                const style = sigToStyle(g.sig);
                                                                const text = highlightTermsInHtml(escapeHtml(g.text), showTermHighlight ? targetTerms : [], 'border border-emerald-500 rounded px-0.5 animate-pulse');
                                                                const aiAttr = g.aiIdx !== undefined ? ` data-ai-idx="${g.aiIdx}"` : '';
                                                                if (style) return `<span style="${style}"${aiAttr}>${text}</span>`;
                                                                return `<span${aiAttr}>${text}</span>`;
                                                            }).join('')
                                                        }}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {/* Load more sentinel */}
                            {visibleCount < paragraphData.length && (
                                <div ref={loadMoreRef} className="flex items-center justify-center py-3 text-xs text-slate-400">
                                    Đang tải thêm... ({visibleCount}/{paragraphData.length})
                                </div>
                            )}
                            {visibleCount >= paragraphData.length && paragraphData.length > BATCH_SIZE && (
                                <div className="text-center py-2 text-[10px] text-slate-300">
                                    Hiện tất cả {paragraphData.length} đoạn
                                </div>
                            )}
                        </div>

                        {/* Debug I/O Panel */}
                        {showDebugIO && (
                            <DebugIOPanel sourceSegments={sourceSegments} aiOutputSegments={currentAiSegments} />
                        )}
                    </div>
                </div>

                {/* Right Column: Sidebar — Minimal design */}
                <div className="w-72 shrink-0 flex flex-col gap-3 overflow-y-auto custom-scrollbar relative z-30 pb-4 text-sm">
                    {/* Header: file meta, compact */}
                    <div className="flex items-center justify-between shrink-0 px-1">
                        <div className="min-w-0 flex items-center gap-2">
                            <button onClick={handleSecretClick} className="text-slate-700 font-semibold truncate text-[13px]" title={fileName}>
                                {fileName}
                            </button>
                            <span className="text-xs text-slate-400 shrink-0">· {currentAiSegments.length}</span>
                        </div>
                        {isDevMode && (
                            <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-red-500 text-white rounded">DEV</span>
                        )}
                    </div>

                    {/* Pending changes — flat warning strip */}
                    {hasPendingChanges && (
                        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
                            <div className="flex items-center gap-2 text-amber-800 text-[13px] font-semibold mb-2">
                                <AlertTriangle className="w-4 h-4 shrink-0" />
                                Có thay đổi chưa lưu
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={handleSavePendingChanges}
                                    className="flex-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-amber-600 text-white hover:bg-amber-700 active:scale-95 transition-all"
                                >
                                    Lưu
                                </button>
                                <button
                                    onClick={() => { setPendingAiSegments(null); setPendingQualityResult(null); }}
                                    className="px-3 py-1.5 rounded-md text-xs font-semibold text-amber-700 hover:bg-amber-100 transition-colors"
                                >
                                    Hủy
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Export — split button: primary DOCX/PPTX + dropdown for other formats */}
                    <div className="flex flex-col gap-2">
                        <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider px-1">Tải file</div>
                        <div className="relative group flex">
                            {/* Primary: file format */}
                            <button
                                onClick={() => confirmBeforeExport(handleExportFile)}
                                disabled={isExporting}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-l-lg text-[13px] font-semibold bg-indigo-600 text-white hover:bg-indigo-700 active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {isExporting
                                    ? <RefreshCw className="w-4 h-4 animate-spin" />
                                    : <Download className="w-4 h-4" />}
                                {isExporting ? 'Đang xuất…' : `Tải ${(originalFormat === 'pptx' || backendFileId?.startsWith('pptx_')) ? 'PPTX' : 'DOCX'}`}
                            </button>
                            {/* Caret → dropdown */}
                            <button
                                disabled={isExporting}
                                aria-label="Chọn định dạng khác"
                                className="px-2 py-2.5 rounded-r-lg border-l border-indigo-500 bg-indigo-600 text-white hover:bg-indigo-700 active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                <ChevronDown className="w-4 h-4" />
                            </button>
                            {/* Dropdown menu */}
                            <div className="absolute right-0 top-full mt-1 w-full min-w-[180px] bg-white border border-slate-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50 overflow-hidden">
                                <button
                                    onClick={() => confirmBeforeExport(() => exportTxt(currentAiSegments, fileName))}
                                    className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors text-left"
                                >
                                    <FileType className="w-3.5 h-3.5 text-slate-400" />
                                    <span>TXT</span>
                                    <span className="ml-auto text-[10px] text-slate-400">Văn bản thuần</span>
                                </button>
                                <button
                                    onClick={() => confirmBeforeExport(handleExportExcel)}
                                    className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors text-left"
                                >
                                    <FileSpreadsheet className="w-3.5 h-3.5 text-slate-400" />
                                    <span>Excel</span>
                                    <span className="ml-auto text-[10px] text-slate-400">Đối chiếu</span>
                                </button>
                                <div className="border-t border-slate-100" />
                                <button
                                    onClick={handleCopyAll}
                                    className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors text-left"
                                >
                                    <Copy className="w-3.5 h-3.5 text-slate-400" />
                                    <span>Sao chép tất cả</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Quality Check */}
                    {onRunQualityCheck && (
                        <div className="flex flex-col gap-2">
                            <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider px-1">Chất lượng</div>
                            {/* Language selector cho QC output — reuse LanguagePicker (search + flag proper) */}
                            {onQcOutputLangChange && (
                                <LanguagePicker
                                    value={qcOutputLang || 'vi'}
                                    onChange={onQcOutputLangChange}
                                    label="Ngôn ngữ feedback"
                                    disabled={isQualityChecking}
                                />
                            )}
                            {currentQualityResult ? (
                                <div className="flex flex-col gap-1.5">
                                    <button
                                        onClick={() => setIsQCDrawerOpen(true)}
                                        disabled={isQualityChecking}
                                        className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-[12px] font-semibold border text-left transition-all bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                                    >
                                        <ShieldCheck className="w-4 h-4 shrink-0" />
                                        <span className="flex-1">Xem báo cáo</span>
                                        <span className={`text-[11px] font-black tabular-nums ${getScoreTextColor(currentQualityResult.overallScore)}`}>
                                            {currentQualityResult.overallScore}/10
                                        </span>
                                    </button>
                                    <button
                                        onClick={onRunQualityCheck}
                                        disabled={isQualityChecking}
                                        title="Đánh giá lại"
                                        className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-all disabled:opacity-60 disabled:cursor-wait"
                                    >
                                        <RefreshCw className={`w-4 h-4 ${isQualityChecking ? 'animate-spin' : ''}`} />
                                        {!isQualityChecking && (
                                            <InlineCostBadge
                                                credits={qcPreflight.credits}
                                                isComputing={qcPreflight.isComputing}
                                                error={qcPreflight.error}
                                            />
                                        )}
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={onRunQualityCheck}
                                    disabled={isQualityChecking}
                                    className={`w-full flex items-start gap-2 px-3 py-2.5 rounded-lg text-[12px] font-medium border text-left transition-all ${
                                        isQualityChecking
                                            ? 'bg-slate-50 text-slate-400 border-slate-200 cursor-wait'
                                            : 'bg-white text-slate-700 border-slate-300 hover:border-indigo-400 hover:text-indigo-600'
                                    }`}
                                >
                                    {isQualityChecking
                                        ? <RefreshCw className="w-4 h-4 animate-spin shrink-0 mt-0.5" />
                                        : <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />}
                                    <span className="flex-1 leading-snug">
                                        {isQualityChecking
                                            ? 'Đang đánh giá…'
                                            : <>Nhờ Quality Check AI Agent đánh giá chất lượng dịch <InlineCostBadge credits={qcPreflight.credits} isComputing={qcPreflight.isComputing} error={qcPreflight.error} compact className="ml-1 align-middle" /></>}
                                    </span>
                                </button>
                            )}
                        </div>
                    )}

                    {/* Batch Status (dưới QC) */}
                    {effectiveBatchInfo && (
                        <div className="flex flex-col gap-2">
                            <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider px-1">Batch dịch</div>
                            <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
                                <BatchStatusPanel
                                    batchInfo={effectiveBatchInfo}
                                    onRetryBatch={onRetryBatch}
                                    onRetryAllFailed={onRetryAllFailed}
                                    sourceSegments={sourceSegments}
                                />
                            </div>
                        </div>
                    )}

                    {/* Tool row — inline minimal */}
                    {(onRetranslate || (sourceTerms.length > 0 || targetTerms.length > 0) || user?.role === 'admin') && (
                        <div className="flex flex-col gap-1 pt-1 border-t border-slate-200">
                            <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider px-1 pt-2">Công cụ</div>

                            {(sourceTerms.length > 0 || targetTerms.length > 0) && (
                                <button
                                    onClick={() => setShowTermHighlight(v => !v)}
                                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-[13px] text-slate-700 hover:bg-slate-50 transition-colors"
                                >
                                    <Sparkles className={`w-3.5 h-3.5 ${showTermHighlight ? 'text-amber-500' : 'text-slate-400'}`} />
                                    <span className="flex-1 text-left">Highlight thuật ngữ</span>
                                    <span className="text-[11px] text-slate-400">{sourceTerms.length}</span>
                                    <div className={`w-8 h-4 rounded-full p-0.5 transition-colors ${showTermHighlight ? 'bg-amber-500' : 'bg-slate-300'}`}>
                                        <div className={`w-3 h-3 bg-white rounded-full transition-transform ${showTermHighlight ? 'translate-x-4' : 'translate-x-0'}`} />
                                    </div>
                                </button>
                            )}

                            {onRetranslate && (
                                <button
                                    onClick={() => {
                                        const msg = hasPendingChanges
                                            ? 'Bạn có thay đổi chưa lưu. Dịch lại TỪ ĐẦU sẽ XÓA toàn bộ bản dịch hiện tại và chỉnh sửa. Tiếp tục?'
                                            : 'Dịch lại TỪ ĐẦU sẽ XÓA toàn bộ bản dịch hiện tại và chạy lại tất cả batch. Tiếp tục?';
                                        if (!confirm(msg)) return;
                                        if (hasPendingChanges) {
                                            setPendingAiSegments(null);
                                            setPendingQualityResult(null);
                                        }
                                        onRetranslate();
                                    }}
                                    disabled={isRetranslating}
                                    className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-[13px] font-bold text-white bg-amber-600 hover:bg-amber-700 shadow-sm ring-1 ring-amber-700/20 transition-colors disabled:opacity-60 disabled:cursor-wait"
                                >
                                    <RefreshCw className={`w-3.5 h-3.5 ${isRetranslating ? 'animate-spin' : ''}`} />
                                    <span className="flex-1 text-left">{isRetranslating ? 'Đang dịch lại…' : 'Dịch lại từ đầu'}</span>
                                    {!isRetranslating && (
                                        <InlineCostBadge
                                            credits={retranslatePreflight.credits}
                                            isComputing={retranslatePreflight.isComputing}
                                            error={retranslatePreflight.error}
                                            compact
                                            className="shrink-0 bg-white/90 text-amber-900 border-amber-300/70"
                                        />
                                    )}
                                </button>
                            )}

                            {user?.role === 'admin' && (
                                <button
                                    onClick={() => setShowDebugIO(!showDebugIO)}
                                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-[13px] text-slate-700 hover:bg-slate-50 transition-colors"
                                >
                                    <Terminal className={`w-3.5 h-3.5 ${showDebugIO ? 'text-slate-800' : 'text-slate-400'}`} />
                                    <span className="flex-1 text-left">Debug I/O</span>
                                    <div className={`w-8 h-4 rounded-full p-0.5 transition-colors ${showDebugIO ? 'bg-slate-800' : 'bg-slate-300'}`}>
                                        <div className={`w-3 h-3 bg-white rounded-full transition-transform ${showDebugIO ? 'translate-x-4' : 'translate-x-0'}`} />
                                    </div>
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* ============================================
                 QC DRAWER — overlay slide-in từ phải
                 ============================================ */}
            {isQCDrawerOpen && currentQualityResult && (
                <>
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 bg-black/20 z-40 animate-in fade-in duration-200"
                        onClick={() => setIsQCDrawerOpen(false)}
                    />
                    {/* Drawer — redesigned: rộng 480px, list-style, overall score hero */}
                    <div className="fixed right-0 top-0 h-full w-[480px] bg-white shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300 border-l border-slate-200">
                        {/* Header — hero overall score */}
                        <div className="p-5 border-b border-slate-200 bg-gradient-to-br from-indigo-50 via-white to-white relative">
                            <button
                                onClick={() => setIsQCDrawerOpen(false)}
                                className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-all"
                                aria-label="Đóng"
                            >
                                <X className="w-5 h-5" />
                            </button>
                            <div className="flex items-center gap-2 mb-3">
                                <ShieldCheck className="w-4 h-4 text-indigo-600" />
                                <h3 className="font-bold text-slate-700 text-xs uppercase tracking-widest">Quality Report</h3>
                                <span className="text-[10px] text-slate-400">· Self-Audit</span>
                            </div>
                            <div className="flex items-baseline gap-2">
                                <span className={`text-5xl font-black tabular-nums leading-none ${getScoreTextColor(currentQualityResult.overallScore)}`}>
                                    {currentQualityResult.overallScore}
                                </span>
                                <span className="text-xl font-bold text-slate-400 tabular-nums">/10</span>
                                <span className="text-xs text-slate-500 ml-auto">Overall</span>
                            </div>
                        </div>

                        {/* Body scroll */}
                        <div className="flex-1 overflow-y-auto custom-scrollbar p-5 space-y-5">
                            {/* Loading */}
                            {isQualityChecking && (
                                <div className="flex items-center gap-2 text-sm text-slate-500 bg-slate-50 rounded-lg p-3 border border-slate-200">
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                    Đang cập nhật báo cáo…
                                </div>
                            )}

                            {/* Component scores — vertical list, full-width */}
                            <div className="space-y-3">
                                <h4 className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Điểm theo tiêu chí</h4>
                                {[
                                    { label: 'Accuracy', score: currentQualityResult.componentScores?.accuracy ?? 0, text: currentQualityResult.accuracyAnalysis || accuracyAnalysis, color: 'blue' },
                                    { label: 'Fluency', score: currentQualityResult.componentScores?.fluency ?? 0, text: currentQualityResult.fluencyAnalysis || '', color: 'violet' },
                                    { label: 'Completeness', score: currentQualityResult.componentScores?.completeness ?? 0, text: currentQualityResult.completenessAnalysis || '', color: 'emerald' },
                                    { label: 'Style', score: currentQualityResult.componentScores?.style ?? currentQualityResult.componentScores?.tone ?? 0, text: currentQualityResult.toneAnalysis || toneAnalysis, color: 'amber' },
                                ].map((item) => (
                                    <div key={item.label} className="group">
                                        <div className="flex items-center gap-3 mb-1.5">
                                            <span className={`text-xs font-bold text-${item.color}-700 w-24 shrink-0`}>
                                                {item.label}
                                            </span>
                                            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                                                <div
                                                    className={`h-full bg-${item.color}-500 rounded-full transition-all duration-500`}
                                                    style={{ width: `${(item.score || 0) * 10}%` }}
                                                />
                                            </div>
                                            <span className={`text-sm font-black tabular-nums text-${item.color}-600 w-10 text-right shrink-0`}>
                                                {item.score}
                                            </span>
                                        </div>
                                        {item.text ? (
                                            <p className="text-xs text-slate-600 leading-relaxed pl-[108px] pr-2">
                                                {item.text}
                                            </p>
                                        ) : (
                                            <p className="text-[11px] text-slate-300 italic pl-[108px]">Chưa có phân tích</p>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Suggested prompt update */}
                            {currentQualityResult.suggestedPromptUpdate && (
                                <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-3 border border-indigo-100">
                                    <div className="flex items-start gap-2">
                                        <Sparkles className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />
                                        <div>
                                            <h4 className="text-[10px] font-black text-indigo-900 uppercase tracking-wider mb-1">Gợi ý cải thiện</h4>
                                            <p className="text-xs text-indigo-800 leading-relaxed">
                                                {currentQualityResult.suggestedPromptUpdate}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Issue list */}
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                        Issues ({sortedIssues.length})
                                    </h4>
                                    {sortedIssues.length > 0 && (
                                        <div className="flex gap-3 text-xs">
                                            {(['critical', 'major', 'minor'] as const).map(sev => {
                                                const count = sortedIssues.filter(i => i.severity === sev).length;
                                                if (!count) return null;
                                                return (
                                                    <span key={sev} className="flex items-center gap-1.5">
                                                        <span className={`w-2 h-2 rounded-full ${sevColors[sev].dot}`} />
                                                        <span className="text-slate-600 tabular-nums font-semibold">{count}</span>
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                {sortedIssues.length === 0 ? (
                                    <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm text-emerald-700">
                                        <CheckCircle2 className="w-5 h-5 shrink-0" />
                                        Không phát hiện issue nào
                                    </div>
                                ) : (
                                    <div className="space-y-2.5">
                                        {sortedIssues.map((issue) => {
                                            const ss = sevColors[issue.severity];
                                            const rowDisplay = aiIdxToParaDisplay.get(issue.aiIdx) ?? (issue.aiIdx + 1);
                                            return (
                                                <button
                                                    key={issue.aiIdx}
                                                    onClick={() => scrollToAiIdx(issue.aiIdx)}
                                                    className={`w-full text-left bg-white border ${ss.border} rounded-xl p-3 hover:shadow-md transition-all`}
                                                >
                                                    {/* Meta row: severity + category + row + score */}
                                                    <div className="flex items-center gap-1.5 flex-wrap mb-2">
                                                        <span className={`inline-flex items-center gap-1 ${ss.bg} ${ss.text} text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${ss.border}`}>
                                                            <span className={`w-1.5 h-1.5 rounded-full ${ss.dot}`} />
                                                            {ss.label}
                                                        </span>
                                                        {issue.category && (
                                                            <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${getCategoryColor(issue.category)}`}>
                                                                {issue.category}
                                                            </span>
                                                        )}
                                                        <span className="text-[10px] text-slate-400 ml-auto tabular-nums font-mono">
                                                            Row #{rowDisplay}
                                                        </span>
                                                        <span className={`text-xs font-bold tabular-nums ${getScoreTextColor(issue.score)}`}>
                                                            {issue.score}/10
                                                        </span>
                                                    </div>

                                                    {/* Description */}
                                                    {issue.description && (
                                                        <p className="text-[13px] text-slate-800 leading-snug mb-2">
                                                            {issue.description}
                                                        </p>
                                                    )}

                                                    {/* Diff box — chỉ show khi current AI !== suggestion (= thật sự cần sửa) */}
                                                    {(() => {
                                                        const normalizedCurrent = (issue.currentTranslation || '').trim();
                                                        const normalizedSuggestion = (issue.suggestionText || '').trim();
                                                        const hasRealDiff = normalizedCurrent && normalizedSuggestion && normalizedCurrent !== normalizedSuggestion;
                                                        const suggestionOnly = !normalizedCurrent && normalizedSuggestion;

                                                        if (hasRealDiff) {
                                                            return (
                                                                <div className="bg-slate-50 rounded-lg border border-slate-200 p-2 space-y-1.5 text-[12px] leading-relaxed">
                                                                    {issue.sourceText && (
                                                                        <div className="flex items-start gap-1.5 text-[11px]">
                                                                            <span className="text-slate-400 shrink-0 font-bold">VI</span>
                                                                            <span className="text-slate-500 italic">{issue.sourceText}</span>
                                                                        </div>
                                                                    )}
                                                                    <div className="flex items-start gap-1.5 font-mono">
                                                                        <span className="text-rose-500 font-bold shrink-0">−</span>
                                                                        <span className="text-rose-700 bg-rose-50 px-1 rounded line-through decoration-rose-300">
                                                                            {normalizedCurrent}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex items-start gap-1.5 font-mono">
                                                                        <span className="text-emerald-600 font-bold shrink-0">+</span>
                                                                        <span className="text-emerald-800 bg-emerald-50 px-1 rounded font-semibold">
                                                                            {normalizedSuggestion}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            );
                                                        }
                                                        if (suggestionOnly) {
                                                            return (
                                                                <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-2 text-[12px] leading-relaxed">
                                                                    <div className="flex items-start gap-1.5 font-mono">
                                                                        <Sparkles className="w-3 h-3 text-emerald-600 shrink-0 mt-0.5" />
                                                                        <span className="text-emerald-800 font-semibold">{normalizedSuggestion}</span>
                                                                    </div>
                                                                </div>
                                                            );
                                                        }
                                                        // current === suggestion → row này dịch đúng, issue là về inconsistency toàn tài liệu
                                                        if (normalizedCurrent) {
                                                            return (
                                                                <div className="flex items-start gap-1.5 text-[11px] text-slate-500 italic mt-1">
                                                                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                                                                    <span>Row này đã dịch đúng (<span className="font-mono font-semibold not-italic text-slate-700">{normalizedCurrent}</span>) — đây là ví dụ về bản dịch chuẩn. Vấn đề là các row khác dùng term không nhất quán.</span>
                                                                </div>
                                                            );
                                                        }
                                                        return null;
                                                    })()}

                                                    {issue.reviews && (
                                                        <p className="text-xs text-slate-500 italic leading-snug mt-2">
                                                            {issue.reviews}
                                                        </p>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer action */}
                        <div className="p-3 border-t border-slate-200 bg-slate-50">
                            <button
                                onClick={onRunQualityCheck}
                                disabled={isQualityChecking}
                                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold bg-white border border-slate-300 text-slate-700 hover:border-indigo-400 hover:text-indigo-600 transition-all disabled:opacity-60 disabled:cursor-wait"
                            >
                                <RefreshCw className={`w-3.5 h-3.5 ${isQualityChecking ? 'animate-spin' : ''}`} />
                                {isQualityChecking ? 'Đang đánh giá…' : 'Đánh giá lại'}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

// ============================================
// HELPER: CSS string → React style object
// ============================================

function cssStringToObj(css: string): React.CSSProperties {
    const obj: Record<string, string> = {};
    for (const part of css.split(';')) {
        const [key, val] = part.split(':');
        if (key && val) {
            const camel = key.trim().replace(/-([a-z])/g, (_, c) => c.toUpperCase());
            obj[camel] = val.trim();
        }
    }
    return obj as any;
}

// ============================================
// BATCH STATUS PANEL (same as V1)
// ============================================

function BatchStatusPanel({ batchInfo, onRetryBatch, onRetryAllFailed, sourceSegments }: {
    batchInfo: import('../../../types').BatchInfo[];
    onRetryBatch?: (batchIndex: number) => void;
    onRetryAllFailed?: () => void;
    sourceSegments?: string[];
}) {
    const doneBatches = batchInfo.filter(b => b.status === 'done');
    const failedBatches = batchInfo.filter(b => b.status !== 'done' && b.status !== 'running');
    const runningBatches = batchInfo.filter(b => b.status === 'running');
    const isRetrying = runningBatches.length > 0;
    const totalSegs = batchInfo.reduce((s, b) => s + b.segmentCount, 0);
    const doneSegs = doneBatches.reduce((s, b) => s + b.segmentCount - (b.missingCount || 0), 0);
    const allDone = doneBatches.length === batchInfo.length;
    const percent = Math.round((doneSegs / Math.max(1, totalSegs)) * 100);

    // Pre-flight cost cho "Retry all failed": flatten segments của failed batches
    const failedSegments = useMemo(() => {
        if (!sourceSegments?.length || failedBatches.length === 0) return [] as string[];
        const out: string[] = [];
        for (const b of failedBatches) {
            const start = b.startSegment ?? 0;
            const end = b.endSegment ?? 0;
            for (let i = start; i < end && i < sourceSegments.length; i++) {
                if (sourceSegments[i]) out.push(sourceSegments[i]);
            }
        }
        return out;
    }, [sourceSegments, failedBatches]);

    const retryPreflight = usePreflightCost({
        type: 'translate',
        sourceSegments: failedSegments,
        enabled: failedSegments.length > 0,
    });

    return (
        <div className="p-3 space-y-2.5">
            <div className="flex items-center gap-2 min-w-0">
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${allDone ? 'bg-emerald-500' : 'bg-red-500 animate-pulse'}`} />
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Batches</span>
                <span className="text-[10px] text-slate-400 tabular-nums">{doneBatches.length}/{batchInfo.length}</span>
            </div>

            <div className="space-y-1">
                <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full transition-all ${allDone ? 'bg-emerald-500' : 'bg-red-500'}`} style={{ width: `${percent}%` }} />
                </div>
                <div className="text-[9px] text-slate-400 tabular-nums">{doneSegs}/{totalSegs} segments · {percent}%</div>
            </div>

            <div className="space-y-1 max-h-48 overflow-y-auto custom-scrollbar -mx-1 px-1">
                {batchInfo.map(b => {
                    const isDone = b.status === 'done';
                    const isError = b.status === 'error';
                    const isRunning = b.status === 'running';
                    return (
                        <div
                            key={b.index}
                            className={`flex items-center gap-2 px-2 py-1.5 rounded-md border text-[10px] ${
                                isDone ? 'bg-emerald-50/50 border-emerald-100' :
                                isRunning ? 'bg-amber-50 border-amber-200' :
                                isError ? 'bg-red-50 border-red-200' :
                                'bg-slate-50 border-slate-200'
                            }`}
                        >
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                                isDone ? 'bg-emerald-500' :
                                isRunning ? 'bg-amber-500 animate-pulse' :
                                isError ? 'bg-red-500' :
                                'bg-slate-400'
                            }`} />
                            <span className="font-bold text-slate-700 shrink-0">B{b.index + 1}</span>
                            <span className="text-slate-400 tabular-nums shrink-0">{b.startSegment + 1}–{b.endSegment}</span>
                            {isRunning && (
                                <span className="text-amber-600 truncate flex-1 min-w-0">· Đang dịch lại…</span>
                            )}
                            {isError && b.error && (
                                <span className="text-red-500 truncate flex-1 min-w-0" title={b.error}>· {b.error}</span>
                            )}
                            {isDone && <span className="text-emerald-600 ml-auto shrink-0">✓</span>}
                            {isRunning && (
                                <RefreshCw className="ml-auto shrink-0 w-2.5 h-2.5 text-amber-500 animate-spin" />
                            )}
                            {!isDone && !isRunning && onRetryBatch && (
                                <button
                                    onClick={() => {
                                        const segs = b.segmentCount;
                                        if (!confirm(`Thử lại batch B${b.index + 1} (${segs} đoạn) sẽ tốn credits. Tiếp tục?`)) return;
                                        onRetryBatch(b.index);
                                    }}
                                    disabled={isRetrying}
                                    className="ml-auto shrink-0 p-0.5 rounded bg-red-500 hover:bg-red-600 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <RefreshCw className="w-2.5 h-2.5" />
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            {failedBatches.length > 0 && onRetryAllFailed && (
                <button
                    onClick={() => {
                        const credits = retryPreflight.credits;
                        const costMsg = credits != null ? ` (~${credits.toLocaleString('vi-VN')} credits)` : '';
                        if (!confirm(`Thử lại ${failedBatches.length} batch lỗi${costMsg}. Tiếp tục?`)) return;
                        onRetryAllFailed();
                    }}
                    disabled={isRetrying}
                    className="w-full flex items-center justify-center gap-2 px-2.5 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-[11px] font-black uppercase tracking-wider transition-colors shadow-sm disabled:opacity-60 disabled:cursor-wait"
                >
                    <RefreshCw className={`w-3 h-3 ${isRetrying ? 'animate-spin' : ''}`} />
                    <span>{isRetrying ? 'Đang thử lại…' : `Thử lại ${failedBatches.length} lỗi`}</span>
                    {!isRetrying && (
                        <InlineCostBadge
                            credits={retryPreflight.credits}
                            isComputing={retryPreflight.isComputing}
                            error={retryPreflight.error}
                            compact
                            className="bg-white/90 text-red-900 border-red-300/70"
                        />
                    )}
                </button>
            )}
        </div>
    );
}

// ============================================
// DEBUG I/O PANEL (same as V1)
// ============================================

function DebugIOPanel({ sourceSegments, aiOutputSegments }: { sourceSegments: string[]; aiOutputSegments: string[] }) {
    const srcLen = sourceSegments.length;
    const aiLen = aiOutputSegments.length;
    const isMatch = srcLen === aiLen;
    const inputPreview = sourceSegments.map((s, i) => `${i + 1} ::: ${s}`).join('\n');
    const outputPreview = aiOutputSegments.map((s, i) => `${i + 1} ::: ${s}`).join('\n');
    const [activeTab, setActiveTab] = useState<'overview' | 'input' | 'output'>('overview');

    const issues: { idx: number; type: string; src: string; ai: string }[] = [];
    for (let i = 0; i < Math.max(srcLen, aiLen); i++) {
        const src = sourceSegments[i] || '';
        const ai = aiOutputSegments[i] || '';
        if (i >= srcLen) issues.push({ idx: i, type: 'EXTRA_AI', src: '', ai });
        else if (i >= aiLen) issues.push({ idx: i, type: 'MISSING_AI', src, ai: '' });
        else if (!ai.trim()) issues.push({ idx: i, type: 'EMPTY', src, ai });
    }

    return (
        <div className="bg-slate-900 rounded-xl p-4 text-xs font-mono space-y-3">
            <div className="flex items-center justify-between">
                <span className="text-amber-400 font-bold uppercase tracking-widest text-[10px]">
                    <Terminal className="w-3 h-3 inline mr-1" />Debug I/O
                </span>
                <div className="flex gap-4 text-slate-400">
                    <span>Source: <span className="text-emerald-400 font-bold">{srcLen}</span></span>
                    <span>Output: <span className={`font-bold ${isMatch ? 'text-emerald-400' : 'text-red-400'}`}>{aiLen}</span></span>
                    <span className={`px-2 py-0.5 rounded ${isMatch ? 'bg-emerald-900 text-emerald-300' : 'bg-red-900 text-red-300'}`}>
                        {isMatch ? 'MATCH' : `DIFF ${aiLen - srcLen > 0 ? '+' : ''}${aiLen - srcLen}`}
                    </span>
                </div>
            </div>
            <div className="flex gap-1 border-b border-slate-700 pb-1">
                {(['overview', 'input', 'output'] as const).map(tab => (
                    <button key={tab} onClick={() => setActiveTab(tab)}
                        className={`px-3 py-1 rounded-t text-[10px] font-bold uppercase ${activeTab === tab ? 'bg-slate-700 text-white' : 'text-slate-500 hover:text-slate-300'}`}
                    >
                        {tab === 'overview' ? 'Overview' : tab === 'input' ? 'AI Input' : 'AI Output'}
                    </button>
                ))}
            </div>
            {activeTab === 'overview' && (
                <div className="space-y-2">
                    {issues.length > 0 ? (
                        <div className="space-y-1">
                            <span className="text-red-400 text-[10px] font-bold uppercase">Issues ({issues.length}):</span>
                            <div className="max-h-48 overflow-y-auto space-y-1">
                                {issues.slice(0, 50).map((issue, i) => (
                                    <div key={i} className="flex gap-2 text-[10px]">
                                        <span className="px-1 rounded bg-amber-900 text-amber-300 shrink-0">{issue.type}</span>
                                        <span className="text-slate-500">#{issue.idx + 1}</span>
                                        <span className="text-blue-300 truncate flex-1" title={issue.src}>"{issue.src.substring(0, 50)}"</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="text-emerald-400 text-[10px]">All {srcLen} segments matched 1:1. No issues.</div>
                    )}
                </div>
            )}
            {activeTab === 'input' && (
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <span className="text-blue-400 text-[10px] font-bold uppercase">Segments sent to AI ({srcLen})</span>
                        <button onClick={() => navigator.clipboard.writeText(inputPreview)} className="text-[10px] text-slate-500 hover:text-white px-2 py-0.5 rounded bg-slate-800">Copy</button>
                    </div>
                    <pre className="max-h-96 overflow-auto bg-slate-950 rounded p-3 text-[10px] text-blue-200 whitespace-pre-wrap select-all">{inputPreview}</pre>
                </div>
            )}
            {activeTab === 'output' && (
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <span className="text-emerald-400 text-[10px] font-bold uppercase">AI output ({aiLen})</span>
                        <button onClick={() => navigator.clipboard.writeText(outputPreview)} className="text-[10px] text-slate-500 hover:text-white px-2 py-0.5 rounded bg-slate-800">Copy</button>
                    </div>
                    <pre className="max-h-96 overflow-auto bg-slate-950 rounded p-3 text-[10px] text-emerald-200 whitespace-pre-wrap select-all">{outputPreview}</pre>
                </div>
            )}
        </div>
    );
}

export default TranslationExportV2;
